import {
  CHANNELS,
  contractCreateSchema,
  LANGUAGES,
  PERIODICITIES,
  policyholderCreateSchema,
} from '@beta-assur/shared';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { ActionError } from '../components/states';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Input, Select } from '../components/ui/input';
import { parseDinars } from '../lib/format';
import { useT } from '../lib/i18n';
import { useApiMutation } from '../lib/query';
import { useReferentials } from '../lib/referentials';

// Création d'un assuré et d'un contrat (portefeuille sans SI, spec 08 §3).

type Errors = Record<string, string>;

/** Erreurs Zod par champ (premier message de chaque chemin). */
export function fieldErrors(issues: { path: PropertyKey[]; message: string }[]): Errors {
  const errors: Errors = {};
  for (const issue of issues) {
    const key = String(issue.path[0] ?? '_');
    errors[key] ??= issue.message;
  }
  return errors;
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

export function PolicyholderCreateDialog() {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: 'person',
    firstName: '',
    lastName: '',
    companyName: '',
    externalRef: '',
    phone: '',
    email: '',
    cin: '',
    preferredChannel: 'whatsapp',
    language: 'fr',
  });
  const [errors, setErrors] = useState<Errors>({});
  const create = useApiMutation<unknown, { id: string }>('POST', '/v1/policyholders', {
    invalidate: ['/v1/policyholders'],
    onSuccess: (created) => {
      setOpen(false);
      void navigate({ to: '/assures/$id', params: { id: created.id } });
    },
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    const body = {
      kind: form.kind,
      ...(form.kind === 'company'
        ? { companyName: orNull(form.companyName) }
        : { firstName: orNull(form.firstName), lastName: orNull(form.lastName) }),
      externalRef: orNull(form.externalRef),
      phone: orNull(form.phone),
      email: orNull(form.email),
      cin: orNull(form.cin),
      preferredChannel: form.preferredChannel,
      language: form.language,
    };
    const parsed = policyholderCreateSchema.safeParse(body);
    if (!parsed.success) {
      const errs = fieldErrors(parsed.error.issues);
      setErrors(
        errs._ ? { ...errs, [form.kind === 'company' ? 'companyName' : 'lastName']: errs._ } : errs,
      );
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t.portfolio.newPolicyholder}
      trigger={<Button size="sm">{t.portfolio.newPolicyholder}</Button>}
    >
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
        <Field label={t.portfolio.kind} className="sm:col-span-2">
          <Select value={form.kind} onChange={set('kind')}>
            <option value="person">{t.portfolio.person}</option>
            <option value="company">{t.portfolio.company}</option>
          </Select>
        </Field>
        {form.kind === 'company' ? (
          <Field
            label={t.portfolio.companyName}
            error={errors.companyName}
            className="sm:col-span-2"
          >
            <Input value={form.companyName} onChange={set('companyName')} />
          </Field>
        ) : (
          <>
            <Field label={t.portfolio.firstName}>
              <Input value={form.firstName} onChange={set('firstName')} />
            </Field>
            <Field label={t.portfolio.lastName} error={errors.lastName}>
              <Input value={form.lastName} onChange={set('lastName')} />
            </Field>
          </>
        )}
        <Field label={t.portfolio.phone} error={errors.phone}>
          <Input type="tel" value={form.phone} onChange={set('phone')} />
        </Field>
        <Field label={t.portfolio.email} error={errors.email}>
          <Input type="email" value={form.email} onChange={set('email')} />
        </Field>
        <Field label={t.portfolio.cin} error={errors.cin}>
          <Input value={form.cin} onChange={set('cin')} />
        </Field>
        <Field label={t.portfolio.externalRef}>
          <Input value={form.externalRef} onChange={set('externalRef')} />
        </Field>
        <Field label={t.portfolio.preferredChannel}>
          <Select value={form.preferredChannel} onChange={set('preferredChannel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t.channels[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.portfolio.language}>
          <Select value={form.language} onChange={set('language')}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t.languages[l]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <ActionError error={create.error} />
        </div>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {t.common.create}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function ContractCreateDialog({ policyholderId }: { policyholderId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const refs = useReferentials();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    insurerId: '',
    productLineId: '',
    agentId: '',
    policyNumber: '',
    effectiveDate: '',
    expiryDate: '',
    periodicity: 'annual',
    premium: '',
    insuredObjectLabel: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const create = useApiMutation<unknown, { id: string }>('POST', '/v1/contracts', {
    invalidate: ['/v1/contracts', '/v1/installments'],
    onSuccess: (created) => {
      setOpen(false);
      void navigate({ to: '/contrats/$id', params: { id: created.id } });
    },
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const products = refs.productLines.filter((p) => p.insurerId === form.insurerId);

  function submit(event: FormEvent) {
    event.preventDefault();
    const premium = parseDinars(form.premium);
    const parsed = contractCreateSchema.safeParse({
      insurerId: form.insurerId,
      productLineId: form.productLineId,
      policyholderId,
      agentId: form.agentId || null,
      policyNumber: form.policyNumber,
      effectiveDate: form.effectiveDate,
      expiryDate: form.expiryDate || null,
      periodicity: form.periodicity,
      premiumAmount: premium ?? -1,
      insuredObjectLabel: orNull(form.insuredObjectLabel),
    });
    if (!parsed.success) {
      const errs = fieldErrors(parsed.error.issues);
      if (errs.premiumAmount) errs.premium = errs.premiumAmount;
      setErrors(errs);
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t.portfolio.newContract}
      trigger={<Button size="sm">{t.portfolio.newContract}</Button>}
    >
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
        <Field label={t.portfolio.insurer} error={errors.insurerId ? t.common.required : undefined}>
          <Select value={form.insurerId} onChange={set('insurerId')}>
            <option value="">-</option>
            {refs.insurers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t.portfolio.product}
          error={errors.productLineId ? t.common.required : undefined}
        >
          <Select value={form.productLineId} onChange={set('productLineId')}>
            <option value="">-</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.labelFr}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.portfolio.policyNumber} error={errors.policyNumber}>
          <Input value={form.policyNumber} onChange={set('policyNumber')} />
        </Field>
        <Field label={t.portfolio.agent}>
          <Select value={form.agentId} onChange={set('agentId')}>
            <option value="">{t.portfolio.noAgent}</option>
            {refs.agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.portfolio.effectiveDate} error={errors.effectiveDate}>
          <Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} />
        </Field>
        <Field label={t.portfolio.expiryDate} error={errors.expiryDate}>
          <Input type="date" value={form.expiryDate} onChange={set('expiryDate')} />
        </Field>
        <Field label={t.portfolio.periodicity}>
          <Select value={form.periodicity} onChange={set('periodicity')}>
            {PERIODICITIES.map((p) => (
              <option key={p} value={p}>
                {t.periodicity[p]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={`${t.portfolio.premium} (DT)`} error={errors.premium}>
          <Input
            inputMode="decimal"
            value={form.premium}
            onChange={set('premium')}
            placeholder="420,000"
          />
        </Field>
        <Field label={t.portfolio.insuredObject} className="sm:col-span-2">
          <Input value={form.insuredObjectLabel} onChange={set('insuredObjectLabel')} />
        </Field>
        <div className="sm:col-span-2">
          <ActionError error={create.error} />
        </div>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {t.common.create}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
